/* eslint-disable @typescript-eslint/no-explicit-any */

import '../accordion/accordion.scss'
import './select-menu.scss'

import { FC, useEffect, useRef, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import { down_icon } from 'src/assets'
import i18n from 'src/i18n'
import { fitem_t } from 'src/types'
import { deep_cmp, deep_copy, find_option, insensitive_substr, join_str } from 'src/utils'

import { InputText } from '../input-text/input-text'

export const SelectMenu: FC<fitem_t> = ({
  id,
  label,
  required,
  disabled,
  capture = '',
  placeholder,
  select_type = 'DEFAULT',
  options: opts = [],
  error,
  fired,
  hidden,
  classes,
  classes_field = 'input input--hoshi',
  classes_error = 'm-0 p-0 mt-1 mb-2 font-12-semibold c-error text-uppercase',
  classes_icon = 'mr-1 float-right input--icon select--icon',
  classes_option = 'py-2 font-20 select--option',
  set_field_value,
}: fitem_t) => {
  const select_ref: any = useRef()

  /**
   * Use State
   */
  const [expanded, set_expanded] = useState(false)
  const [options, set_options] = useState(opts)
  const [prevopts, set_prevopts] = useState(opts)
  const [selected_key, set_selected_key] = useState(capture)
  const [selected_val, set_selected_val] = useState(find_option(opts, capture)?.val || '')
  const [focus, set_focus] = useState(false)

  /**
   * Alias
   */
  const default_ = select_type !== 'SEARCH'
  const classes_ = {
    field: join_str(
      classes_field,
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      select_type === 'DEFAULT' ? 'z-index-unset' : ''
    ),
    iconl: join_str(classes_icon, expanded ? 'select--rotate' : ''),
    error: join_str(classes_error || 'd-none', fired && error ? '' : 'opacity-0'),
    disabled: disabled ? 'bg-fafafa' : 'bg-ffffff',
  }

  /**
   * Use Effect
   */
  useEffect(() => {
    if (!deep_cmp(opts, options) && (default_ || !selected_val)) {
      set_options(opts)
    }
  }, [hidden, opts, options, selected_val])

  useEffect(() => {
    make_selection()
  }, [capture])

  useEffect(() => {
    if (!deep_cmp(prevopts, opts)) {
      set_prevopts(opts)
      make_selection()
    }
  }, [opts])

  useEffect(() => {
    set_field_value?.(id, selected_key)
  }, [selected_key])

  useEffect(() => {
    if (selected_val && select_type === 'SEARCH') {
      set_options(opts?.filter((opt_) => insensitive_substr(opt_?.val, selected_val)))
    }
  }, [hidden, selected_val])

  useEffect(() => {
    const on_outside_press = (event: Event) => {
      if (select_ref?.current && !select_ref?.current?.contains?.(event.target)) {
        set_expanded(false)
      }
    }
    document?.addEventListener?.('mousedown', on_outside_press)
    return () => {
      document?.removeEventListener?.('mousedown', on_outside_press)
    }
  }, [select_ref])

  /**
   * Support functions
   */
  const make_selection = () => {
    set_selected_key(capture)
    set_selected_val(find_option(opts, capture)?.val || '')
  }

  /**
   * Render functions
   */
  const render_input = () => (
    <InputText
      id={id}
      label={label}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      input_type={default_ ? 'SELECT' : 'DEFAULT'}
      classes_field={classes_.field}
      classes_disabled={classes_.disabled}
      classes_icon={classes_.iconl}
      classes_error=""
      icon={down_icon}
      icon_size={14}
      capture={selected_val}
      on_change={set_selected_val}
      on_focus_capture={(isFocused) => {
        set_focus(isFocused)
      }}
      on_press={() => {
        //if the input is enabled to be modified is closed only when the focus is lost
        //if the input is disabled, the dropdown is opened and closed by clicking on the input
        if (default_) {
          if (!disabled) {
            set_expanded(!expanded)
          }
        } else {
          if (!disabled && focus) {
            set_expanded(true)
          } else if (!disabled && !focus) {
            set_expanded(false)
          }
        }
      }}
    />
  )

  const render_options = () => {
    const options_ = required
      ? deep_copy(options)
      : [{ key: '', val: `--${i18n.t('commons.select')}--` }, ...deep_copy(options || [])]
    return (
      <Collapse in={expanded}>
        <div className="mt-n3">
          {options_?.map((option, idx) => (
            <div
              key={idx}
              id={`${id}_${option?.key}`}
              role="button"
              className={join_str(
                classes_option,
                option?.key === '' ? 'c-7f7f7f' : option?.key === selected_key ? 'bg-e5e5e5' : ''
              )}
              onClick={() => {
                set_selected_key(option?.key)
                set_selected_val(option?.key ? option?.val : '')
                set_expanded(false)
              }}
            >
              {option?.val}
            </div>
          ))}
        </div>
      </Collapse>
    )
  }

  const render_error = () => (
    <p className={classes_.error}>
      {error}
      <span className="opacity-0">-</span>
    </p>
  )

  return (
    <main className={classes}>
      <div ref={select_ref} className="d-flex flex-column">
        {render_input()}
        {render_options()}
        {render_error()}
      </div>
    </main>
  )
}
